import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Heart, ArrowRight, ArrowLeft, Sparkles, Check, X as XIcon,
  Calendar, MapPin, Users, Wallet, Mail, ShieldCheck, SkipForward,
} from 'lucide-react';
import client from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import PrivacyBadge from '../components/PrivacyBadge';

const TOTAL_STEPS = 5;

// Curated wedding inspiration imagery (Unsplash hotlinks — safe to replace later)
const INSPO_DECK = [
  { id: 'modern-minimal',     tag: 'Modern',   img: 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=800&q=70', caption: 'Modern & minimal' },
  { id: 'rustic-barn',        tag: 'Rustic',   img: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?auto=format&fit=crop&w=800&q=70', caption: 'Rustic barn warmth' },
  { id: 'classic-romantic',   tag: 'Classic',  img: 'https://images.unsplash.com/photo-1525772764200-be829a350797?auto=format&fit=crop&w=800&q=70', caption: 'Classic & romantic' },
  { id: 'boho-meadow',        tag: 'Boho',     img: 'https://images.unsplash.com/photo-1606216794074-735e91aa2c92?auto=format&fit=crop&w=800&q=70', caption: 'Boho meadow' },
  { id: 'garden-greenery',    tag: 'Garden',   img: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&w=800&q=70', caption: 'Garden & greenery' },
  { id: 'beach-coast',        tag: 'Coastal',  img: 'https://images.unsplash.com/photo-1469371670807-013ccf25f16a?auto=format&fit=crop&w=800&q=70', caption: 'Coastal & breezy' },
  { id: 'industrial-loft',    tag: 'Industrial', img: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&w=800&q=70', caption: 'Industrial loft' },
  { id: 'candlelit-evening',  tag: 'Moody',    img: 'https://images.unsplash.com/photo-1522413452208-996ff3f3e740?auto=format&fit=crop&w=800&q=70', caption: 'Candlelit & moody' },
  { id: 'pastel-spring',      tag: 'Pastel',   img: 'https://images.unsplash.com/photo-1507504031003-b417219a0fde?auto=format&fit=crop&w=800&q=70', caption: 'Pastel & airy' },
  { id: 'vineyard-sunset',    tag: 'Vineyard', img: 'https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?auto=format&fit=crop&w=800&q=70', caption: 'Vineyard sunset' },
];

const GUEST_BUCKETS = [
  { value: 25,  label: 'Intimate', sub: 'up to 25' },
  { value: 60,  label: 'Cozy', sub: '25 – 60' },
  { value: 120, label: 'Lively', sub: '60 – 120' },
  { value: 200, label: 'Big party', sub: '120 – 200' },
  { value: 300, label: 'Grand', sub: '200+' },
];

const BUDGET_BUCKETS = [
  { value: 10000, label: '$10k or less' },
  { value: 20000, label: '$10k – $25k' },
  { value: 35000, label: '$25k – $50k' },
  { value: 75000, label: '$50k – $100k' },
  { value: 150000, label: '$100k+' },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const { toast } = useToast();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    partner1: user?.partner1 || '',
    partner2: user?.partner2 || '',
    weddingDate: '',
    weddingTime: '',
    location: '',
    guestEstimate: null,
    budgetTotal: null,
    styleTags: [],
    vibe: '',
    partnerEmail: '',
  });

  // Tinder-style deck state
  const [deckIdx, setDeckIdx] = useState(0);
  const [swipeDir, setSwipeDir] = useState(null); // 'like' | 'pass' | null

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get('/onboarding/status');
        if (data.onboarded) {
          navigate('/dashboard', { replace: true });
          return;
        }
        setForm((f) => ({
          ...f,
          partner1: data.partner1 || f.partner1,
          partner2: data.partner2 || f.partner2,
          weddingDate: data.weddingDate || f.weddingDate,
          weddingTime: data.weddingTime || f.weddingTime,
          location: data.location || f.location,
          guestEstimate: data.guestEstimate ?? f.guestEstimate,
          budgetTotal: data.budgetTotal ?? f.budgetTotal,
          styleTags: data.styleTags?.length ? data.styleTags : f.styleTags,
          vibe: data.vibe || f.vibe,
        }));
      } catch (e) { /* ignore */ }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const next = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const handleSwipe = (dir) => {
    setSwipeDir(dir);
    const card = INSPO_DECK[deckIdx];
    if (dir === 'like' && card) {
      setForm((f) => ({
        ...f,
        styleTags: f.styleTags.includes(card.tag) ? f.styleTags : [...f.styleTags, card.tag],
      }));
    }
    setTimeout(() => {
      setSwipeDir(null);
      setDeckIdx((i) => i + 1);
    }, 280);
  };

  const finalize = async () => {
    setSubmitting(true);
    try {
      // Derive vibe = the most-liked tag (or first liked)
      const vibe = form.styleTags[0] || '';
      const payload = {
        partner1: form.partner1 || (user?.email || '').split('@')[0],
        partner2: form.partner2 || '',
        weddingDate: form.weddingDate || '',
        weddingTime: form.weddingTime || '',
        location: form.location || '',
        guestEstimate: form.guestEstimate ?? undefined,
        budgetTotal: form.budgetTotal ?? undefined,
        styleTags: form.styleTags,
        vibe,
        partnerEmail: form.partnerEmail || undefined,
      };
      const { data } = await client.post('/onboarding/complete', payload);
      await refresh();
      if (data?.invite) {
        toast({ title: 'Invite queued', description: `We saved an invite for ${form.partnerEmail}. (Email dispatch is mocked for now.)` });
      } else {
        toast({ title: 'You\u2019re all set 💝', description: 'Welcome to your wedding workspace.' });
      }
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast({ title: 'Hmm, that didn\u2019t save', description: err?.response?.data?.detail || err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const stepValid = useMemo(() => {
    if (step === 0) return true; // welcome
    if (step === 1) return Boolean(form.partner1) && Boolean(form.partner2 || form.partner2 === '');
    if (step === 2) return Boolean(form.guestEstimate) && Boolean(form.budgetTotal);
    if (step === 3) return true; // style quiz can be skipped
    if (step === 4) return true; // partner invite optional
    return true;
  }, [step, form]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF7F2] flex items-center justify-center">
        <div className="w-10 h-10 rounded-full bg-[#7B2C3B] flex items-center justify-center animate-pulse">
          <Heart className="w-5 h-5 text-[#FBF7F2] fill-[#FBF7F2]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF7F2] flex flex-col">
      {/* Header */}
      <header className="px-6 lg:px-10 pt-6 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-[#7B2C3B] flex items-center justify-center">
            <Heart className="w-4 h-4 text-[#FBF7F2] fill-[#FBF7F2]" />
          </div>
          <span className="font-serif text-xl font-semibold">
            Yourwedding<span className="italic text-[#7B2C3B]">friends</span>
          </span>
        </div>
        <PrivacyBadge />
      </header>

      {/* Progress bar */}
      <div className="px-6 lg:px-10 pb-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-2 text-xs text-[#6A6A6A]">
            <span data-testid="onboarding-step-label">Step {Math.min(step + 1, TOTAL_STEPS)} of {TOTAL_STEPS}</span>
            <button
              data-testid="onboarding-skip-all"
              onClick={async () => {
                setSubmitting(true);
                try {
                  await client.post('/onboarding/complete', { styleTags: [] });
                  await refresh();
                } catch (e) { /* ignore */ }
                navigate('/dashboard', { replace: true });
              }}
              className="text-[#9A8A7E] hover:text-[#7B2C3B] inline-flex items-center gap-1"
            >
              <SkipForward className="w-3 h-3" /> Skip for now
            </button>
          </div>
          <div className="h-1.5 rounded-full bg-[#F4E4DA] overflow-hidden">
            <div
              className="h-full bg-[#7B2C3B] transition-all duration-500"
              style={{ width: `${((step + 1) / TOTAL_STEPS) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Body */}
      <main className="flex-1 px-6 lg:px-10 pb-10">
        <div className="max-w-2xl mx-auto">
          {step === 0 && (
            <WelcomeStep
              onStart={next}
              partner1={form.partner1}
              email={user?.email}
            />
          )}
          {step === 1 && (
            <DateLocationStep form={form} setForm={setForm} />
          )}
          {step === 2 && (
            <BucketsStep form={form} setForm={setForm} />
          )}
          {step === 3 && (
            <StyleQuizStep
              deckIdx={deckIdx}
              swipeDir={swipeDir}
              onSwipe={handleSwipe}
              likedTags={form.styleTags}
              onClearLast={() => setForm((f) => ({ ...f, styleTags: f.styleTags.slice(0, -1) }))}
            />
          )}
          {step === 4 && (
            <PartnerInviteStep form={form} setForm={setForm} />
          )}
        </div>
      </main>

      {/* Footer nav */}
      <footer className="border-t border-[#EFE3D8] bg-[#FBF7F2]/95 backdrop-blur sticky bottom-0">
        <div className="max-w-2xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <button
            data-testid="onboarding-back"
            onClick={back}
            disabled={step === 0}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm text-[#6A6A6A] hover:text-[#7B2C3B] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step < TOTAL_STEPS - 1 ? (
            <button
              data-testid="onboarding-next"
              onClick={next}
              disabled={!stepValid}
              className="btn-primary inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium disabled:opacity-60"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              data-testid="onboarding-finish"
              onClick={finalize}
              disabled={submitting}
              className="btn-primary inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium disabled:opacity-60"
            >
              {submitting ? 'Setting up…' : (<>Open my dashboard <ArrowRight className="w-4 h-4" /></>)}
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}

// ---------- Step 0 ----------
function WelcomeStep({ onStart, partner1, email }) {
  const greeting = partner1 || (email || '').split('@')[0] || 'friend';
  return (
    <div className="animate-float-up text-center py-8">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B] text-xs font-medium mb-6">
        <Sparkles className="w-3.5 h-3.5" /> A 60-second setup
      </div>
      <h1 className="font-serif text-4xl sm:text-5xl text-[#2A2A2A] leading-tight">
        Hi {greeting}, congratulations.
      </h1>
      <p className="font-serif italic text-2xl text-[#7B2C3B] mt-1">Let&apos;s plan something beautiful.</p>
      <p className="mt-6 text-[#4A4A4A] text-base max-w-md mx-auto leading-relaxed">
        A few quick questions and we&apos;ll set up your private workspace — budget, guests, vendors, the whole thing — and tune your AI friend to your day.
      </p>
      <button
        data-testid="onboarding-start"
        onClick={onStart}
        className="btn-primary inline-flex items-center gap-2 mt-8 px-7 py-3.5 rounded-full text-sm font-medium"
      >
        Let&apos;s do it <ArrowRight className="w-4 h-4" />
      </button>
      <p className="mt-6 text-xs text-[#9A8A7E] inline-flex items-center gap-1.5 justify-center">
        <ShieldCheck className="w-3.5 h-3.5" /> Private workspace · We never sell your data
      </p>
    </div>
  );
}

// ---------- Step 1 ----------
function DateLocationStep({ form, setForm }) {
  return (
    <div className="animate-float-up py-4">
      <h2 className="font-serif text-3xl text-[#2A2A2A]">The basics first.</h2>
      <p className="text-sm text-[#6A6A6A] mt-1">Don&apos;t have all the answers yet? Just put what you know — you can change anything later.</p>

      <div className="mt-7 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs uppercase tracking-wide text-[#6A6A6A] mb-1.5">Your name</label>
            <input
              data-testid="onboarding-partner1"
              value={form.partner1}
              onChange={(e) => setForm({ ...form, partner1: e.target.value })}
              placeholder="e.g. Maya"
              className="w-full px-4 py-3 rounded-xl border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wide text-[#6A6A6A] mb-1.5">Partner&apos;s name</label>
            <input
              data-testid="onboarding-partner2"
              value={form.partner2}
              onChange={(e) => setForm({ ...form, partner2: e.target.value })}
              placeholder="e.g. Jordan"
              className="w-full px-4 py-3 rounded-xl border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
            />
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wide text-[#6A6A6A] mb-1.5 flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" /> Wedding date (or your best guess)
          </label>
          <input
            data-testid="onboarding-date"
            type="date"
            value={form.weddingDate}
            onChange={(e) => setForm({ ...form, weddingDate: e.target.value })}
            className="w-full px-4 py-3 rounded-xl border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-wide text-[#6A6A6A] mb-1.5 flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" /> Ceremony start time
          </label>
          <input
            data-testid="onboarding-time"
            value={form.weddingTime || ''}
            onChange={(e) => setForm({ ...form, weddingTime: e.target.value })}
            placeholder="e.g. 4:30 PM"
            className="w-full px-4 py-3 rounded-xl border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-wide text-[#6A6A6A] mb-1.5 flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" /> Where? (City or venue area)
          </label>
          <input
            data-testid="onboarding-location"
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            placeholder="e.g. Napa, CA or somewhere in Tuscany…"
            className="w-full px-4 py-3 rounded-xl border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
          />
        </div>
      </div>
    </div>
  );
}

// ---------- Step 2 ----------
function BucketsStep({ form, setForm }) {
  return (
    <div className="animate-float-up py-4">
      <h2 className="font-serif text-3xl text-[#2A2A2A]">A rough scope.</h2>
      <p className="text-sm text-[#6A6A6A] mt-1">Just a ballpark — your AI friend will tune everything around this.</p>

      <div className="mt-7">
        <h3 className="text-xs uppercase tracking-wide text-[#6A6A6A] mb-3 inline-flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" /> Guest size
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {GUEST_BUCKETS.map((b) => {
            const active = form.guestEstimate === b.value;
            return (
              <button
                key={b.value}
                data-testid={`onboarding-guests-${b.value}`}
                onClick={() => setForm({ ...form, guestEstimate: b.value })}
                className={`p-3 rounded-2xl border text-center transition-all ${
                  active
                    ? 'border-[#7B2C3B] bg-[#7B2C3B] text-[#FBF7F2] shadow-md'
                    : 'border-[#EFE3D8] bg-white hover:border-[#B8896C] text-[#2A2A2A]'
                }`}
              >
                <p className="font-serif text-lg leading-none">{b.label}</p>
                <p className={`text-[11px] mt-1 ${active ? 'text-[#F4D6CE]' : 'text-[#9A8A7E]'}`}>{b.sub}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-7">
        <h3 className="text-xs uppercase tracking-wide text-[#6A6A6A] mb-3 inline-flex items-center gap-1.5">
          <Wallet className="w-3.5 h-3.5" /> Overall budget
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
          {BUDGET_BUCKETS.map((b) => {
            const active = form.budgetTotal === b.value;
            return (
              <button
                key={b.value}
                data-testid={`onboarding-budget-${b.value}`}
                onClick={() => setForm({ ...form, budgetTotal: b.value })}
                className={`p-3 rounded-2xl border text-center transition-all ${
                  active
                    ? 'border-[#7B2C3B] bg-[#7B2C3B] text-[#FBF7F2] shadow-md'
                    : 'border-[#EFE3D8] bg-white hover:border-[#B8896C] text-[#2A2A2A]'
                }`}
              >
                <p className="text-sm font-medium">{b.label}</p>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------- Step 3: Tinder-style swipe ----------
function StyleQuizStep({ deckIdx, swipeDir, onSwipe, likedTags, onClearLast }) {
  const done = deckIdx >= INSPO_DECK.length;
  const current = INSPO_DECK[deckIdx];
  const upcoming = INSPO_DECK.slice(deckIdx + 1, deckIdx + 3);

  useEffect(() => {
    if (done) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') onSwipe('pass');
      else if (e.key === 'ArrowRight') onSwipe('like');
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [done, onSwipe]);

  return (
    <div className="animate-float-up py-2">
      <h2 className="font-serif text-3xl text-[#2A2A2A]">What feels like you?</h2>
      <p className="text-sm text-[#6A6A6A] mt-1">Swipe through inspiration. Heart what you love, pass what you don&apos;t — we&apos;ll learn your vibe.</p>

      {!done ? (
        <>
          <div className="mt-6 relative h-[420px] sm:h-[460px]">
            {/* Stacked cards (behind) */}
            {upcoming.reverse().map((c, i) => (
              <div
                key={c.id}
                className="absolute inset-0 mx-auto w-full max-w-sm rounded-3xl overflow-hidden shadow-lg border border-[#EFE3D8] bg-white"
                style={{
                  transform: `translateY(${(upcoming.length - i) * 10}px) scale(${1 - (upcoming.length - i) * 0.03})`,
                  zIndex: 1 + i,
                  opacity: 0.7 - i * 0.2,
                }}
              >
                <img src={c.img} alt="" className="w-full h-full object-cover" />
              </div>
            ))}

            {/* Active card */}
            <div
              data-testid="style-card"
              className={`absolute inset-0 mx-auto w-full max-w-sm rounded-3xl overflow-hidden shadow-xl border border-[#EFE3D8] bg-white transition-all duration-300 ${
                swipeDir === 'like' ? 'translate-x-[120%] rotate-12 opacity-0 pointer-events-none' :
                swipeDir === 'pass' ? '-translate-x-[120%] -rotate-12 opacity-0 pointer-events-none' :
                ''
              }`}
              style={{ zIndex: 10 }}
            >
              <div className="relative w-full h-full">
                <img src={current.img} alt={current.caption} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/95 backdrop-blur text-[#7B2C3B] text-xs font-medium">
                  {current.tag}
                </div>
                <div className="absolute bottom-5 left-5 right-5 text-[#FBF7F2]">
                  <p className="font-serif text-2xl leading-tight">{current.caption}</p>
                  <p className="text-xs opacity-80 mt-1">{deckIdx + 1} of {INSPO_DECK.length}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Controls — sibling below the card stack, always visible */}
          <div data-testid="style-controls" className="mt-6 flex items-center justify-center gap-5">
            <button
              data-testid="style-pass"
              onClick={() => onSwipe('pass')}
              className="w-14 h-14 rounded-full bg-white border border-[#EFE3D8] shadow-md flex items-center justify-center hover:border-[#6A6A6A] transition-colors"
              aria-label="Pass"
              type="button"
            >
              <XIcon className="w-6 h-6 text-[#6A6A6A]" />
            </button>
            <button
              data-testid="style-like"
              onClick={() => onSwipe('like')}
              className="w-16 h-16 rounded-full bg-[#7B2C3B] shadow-lg flex items-center justify-center hover:bg-[#6A2530] transition-colors"
              aria-label="Like"
              type="button"
            >
              <Heart className="w-7 h-7 text-[#FBF7F2] fill-[#FBF7F2]" />
            </button>
          </div>
          <p className="mt-3 text-center text-[11px] text-[#9A8A7E]">
            Or use your keyboard: <kbd className="px-1.5 py-0.5 rounded bg-white border border-[#EFE3D8]">←</kbd> pass · <kbd className="px-1.5 py-0.5 rounded bg-white border border-[#EFE3D8]">→</kbd> love
          </p>
        </>
      ) : (
        <div className="mt-6 soft-card rounded-3xl p-6 text-center bg-white">
          <Sparkles className="w-8 h-8 mx-auto text-[#7B2C3B]" />
          <h3 className="font-serif text-2xl text-[#2A2A2A] mt-3">Got it.</h3>
          <p className="text-sm text-[#6A6A6A] mt-1">Here&apos;s the vibe we&apos;re reading from you:</p>
          {likedTags.length === 0 ? (
            <p className="mt-4 text-sm text-[#9A8A7E] italic">No strong preferences yet — we&apos;ll mix it up for you.</p>
          ) : (
            <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
              {likedTags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B] text-sm">
                  <Check className="w-3.5 h-3.5" /> {t}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {likedTags.length > 0 && !done && (
        <div className="mt-6 flex flex-wrap items-center justify-center gap-1.5">
          <span className="text-xs text-[#9A8A7E] mr-1">You loved:</span>
          {likedTags.map((t) => (
            <span key={t} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F4E4DA] text-[#7B2C3B] text-xs">
              <Heart className="w-3 h-3 fill-[#7B2C3B]" /> {t}
            </span>
          ))}
          <button onClick={onClearLast} className="text-xs text-[#9A8A7E] hover:text-[#7B2C3B] ml-1 underline">undo last</button>
        </div>
      )}
    </div>
  );
}

// ---------- Step 4 ----------
function PartnerInviteStep({ form, setForm }) {
  return (
    <div className="animate-float-up py-4">
      <h2 className="font-serif text-3xl text-[#2A2A2A]">Invite your partner?</h2>
      <p className="text-sm text-[#6A6A6A] mt-1">Plan together. They&apos;ll get their own login and can edit anything you can.</p>

      <div className="mt-6 soft-card rounded-3xl bg-white border border-[#EFE3D8] p-6">
        <div className="flex items-center gap-2 mb-4 text-sm text-[#2A2A2A]">
          <Mail className="w-4 h-4 text-[#7B2C3B]" />
          <span className="font-medium">Send an invite link</span>
        </div>
        <input
          data-testid="onboarding-partner-email"
          type="email"
          placeholder={form.partner2 ? `${form.partner2.toLowerCase()}@email.com` : 'partner@email.com'}
          value={form.partnerEmail}
          onChange={(e) => setForm({ ...form, partnerEmail: e.target.value })}
          className="w-full px-4 py-3 rounded-xl border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
        />
        <p className="text-[11px] text-[#9A8A7E] mt-2">
          We&apos;ll queue the invite and send it once email delivery is wired (Mocked for now). You can always invite them later from Settings.
        </p>
      </div>

      <div className="mt-5 inline-flex items-center gap-2 text-xs text-[#6A6A6A]">
        <ShieldCheck className="w-3.5 h-3.5 text-[#7B2C3B]" />
        Only you and your partner will have access. We never share your details with vendors or advertisers.
      </div>
    </div>
  );
}
